import {
  ActorModel,
  FhirBundle,
  FhirCodeableConcept,
  FhirHumanName,
  FhirResource,
  SupportedActorResource,
  SupportedActorType,
} from "./fhir.types";

const supportedActorResourceTypes = [
  "Patient",
  "Practitioner",
  "Location",
  "Device",
] as const;

export function getBundleResources<T extends FhirResource>(
  bundle: FhirBundle,
  resourceType: T["resourceType"],
): T[] {
  return (bundle.entry ?? [])
    .map((entry) => entry.resource)
    .filter(
      (resource): resource is T =>
        resource?.resourceType === resourceType && Boolean(resource.id),
    );
}

export function isSupportedActorResource(
  resource: FhirResource | undefined,
): resource is SupportedActorResource {
  return Boolean(
    resource &&
      supportedActorResourceTypes.includes(
        resource.resourceType as (typeof supportedActorResourceTypes)[number],
      ) &&
      resource.id,
  );
}

export function actorTypeFromResourceType(
  resourceType: string,
): SupportedActorType | undefined {
  switch (resourceType) {
    case "Patient":
      return "patient";
    case "Practitioner":
      return "practitioner";
    case "Location":
      return "location";
    case "Device":
      return "device";
    default:
      return undefined;
  }
}

export function parseReference(
  reference: string | undefined,
): { resourceType: string; id: string; key: string } | undefined {
  if (!reference) {
    return undefined;
  }

  const normalized = reference.split("?")[0].split("#")[0].replace(/\/$/, "");
  const match = normalized.match(/(?:^|\/)([A-Z][A-Za-z]+)\/([^/]+)$/);

  if (!match) {
    return undefined;
  }

  return {
    resourceType: match[1],
    id: match[2],
    key: `${match[1]}/${match[2]}`,
  };
}

export function referenceKey(reference: string | undefined): string | undefined {
  return parseReference(reference)?.key;
}

export function displayHumanName(names: FhirHumanName[] | undefined): string {
  if (!names?.length) {
    return "";
  }

  const selected =
    names.find((name) => name.use === "official") ??
    names.find((name) => name.use === "usual") ??
    names[0];

  if (selected.text?.trim()) {
    return selected.text.trim();
  }

  return [
    ...(selected.prefix ?? []),
    ...(selected.given ?? []),
    selected.family,
    ...(selected.suffix ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
}

export function displayCodeableConcept(
  concept: FhirCodeableConcept | undefined,
): string | undefined {
  const text = concept?.text?.trim();
  if (text) {
    return text;
  }

  return concept?.coding
    ?.map((coding) => coding.display?.trim() || coding.code?.trim())
    .find(Boolean);
}

export function displayCodeableConcepts(
  concepts: FhirCodeableConcept[] | undefined,
): string[] {
  return (concepts ?? [])
    .map(displayCodeableConcept)
    .filter((value): value is string => Boolean(value));
}

function actorDisplay(resource: SupportedActorResource): string {
  switch (resource.resourceType) {
    case "Patient":
    case "Practitioner":
      return displayHumanName(resource.name) || `${resource.resourceType}/${resource.id}`;
    case "Location":
      return resource.name?.trim() || `Location/${resource.id}`;
    case "Device":
      return (
        resource.deviceName?.find((name) => name.type === "user-friendly-name")
          ?.name?.trim() ||
        resource.deviceName?.[0]?.name?.trim() ||
        `Device/${resource.id}`
      );
  }
}

function actorSubtitle(resource: SupportedActorResource): string | undefined {
  switch (resource.resourceType) {
    case "Patient":
      return undefined;
    case "Practitioner":
      return resource.qualification
        ?.map((qualification) => displayCodeableConcept(qualification.code))
        .find(Boolean);
    case "Location":
      return (
        resource.description?.trim() ||
        resource.type?.map(displayCodeableConcept).find(Boolean)
      );
    case "Device":
      return [
        resource.manufacturer?.trim(),
        resource.modelNumber?.trim(),
        displayCodeableConcept(resource.type),
      ]
        .filter(Boolean)
        .join(" · ") || undefined;
  }
}

export function mapActorResource(resource: SupportedActorResource): ActorModel {
  const type = actorTypeFromResourceType(resource.resourceType);

  if (!type || !resource.id) {
    throw new Error(`Unsupported actor resource: ${resource.resourceType}`);
  }

  const active =
    resource.resourceType === "Location" || resource.resourceType === "Device"
      ? resource.status !== "inactive" && resource.status !== "entered-in-error"
      : resource.active !== false;

  return {
    id: resource.id,
    reference: `${resource.resourceType}/${resource.id}`,
    type,
    display: actorDisplay(resource),
    subtitle: actorSubtitle(resource),
    active,
  };
}

export function deduplicateById<T extends { id: string }>(items: T[]): T[] {
  const byId = new Map<string, T>();
  items.forEach((item) => byId.set(item.id, item));
  return Array.from(byId.values());
}

export function chunk<T>(items: readonly T[], size: number): T[][] {
  const result: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size));
  }
  return result;
}
